import config from "next/config";
import { useMemo } from "react";
import { useSwipeable } from "react-swipeable";

import type { Media } from "@/types/product";

import useStore from "../stateManagement/store";
import useModal from "./useModal";

const useMediaGallery = (gallery?: string) => {
  const setSelectedArray = useStore((state) => state.setSelectedArray);
  const setAllMedia = useStore((state) => state.setAllMedia); // [{name, array}, {name, array}]

  const setSelectedMedia = useStore((state) => state.setSelectedMedia);
  const allMedia = useStore((state) => state.allMedia);
  const selectedMedia = useStore((state) => state.selectedMedia);
  const selectedArray = useStore((state) => state.selectedArray);

  const currentArray = allMedia[selectedArray];

  const currentMedia = currentArray?.media;

  const modal = useModal("mediaGallery");

  const selectArray = (arrayName?: string) => {
    if (arrayName) {
      setSelectedMedia(0);
      const arrayIndex = allMedia.findIndex(
        (mediaObject) => mediaObject.name === arrayName
      );
      setSelectedArray(arrayIndex);
      return arrayIndex;
    }
    const arrayIndex = allMedia.findIndex(
      (mediaObject) => mediaObject.name === gallery
    );
    setSelectedArray(arrayIndex);
    return arrayIndex;
  };

  const findMedia = (media: Media) => {
    const mediaArray = allMedia[selectArray()]?.media;
    if (mediaArray) {
      const index = mediaArray?.findIndex((m: Media) => m.uri === media.uri);
      if (index !== undefined && index !== -1) {
        setSelectedMedia(index);
        modal.openModal();
      }
    }
  };

  const carouselCallback = (index: number) => {
    setSelectedMedia(index);
  };

  const increment = () => {
    try {
      const index = selectedMedia || 0;
      const next = index + 1;
      if (currentMedia && next < currentMedia.length) {
        return setSelectedMedia(next);
      }
      return setSelectedMedia(0);
    } catch (error) {
      return console.error(error);
    }
  };

  const decrement = () => {
    const index = selectedMedia || 0;
    const next = index - 1;
    if (next >= 0) {
      return setSelectedMedia(next);
    }
    setSelectedMedia((currentMedia && currentMedia?.length - 1) || 0);
    return;
  };

  const close = () => {
    modal.closeModal();
    setSelectedMedia(null);
  };

  const isActive = (index: number) => {
    return index === selectedMedia;
  };

  const media = currentMedia?.map((m, i) => {
    return {
      ...m,
      callback: () => carouselCallback(i),
      active: isActive(i),
      value: i + 1,
    };
  });

  const carousels = useMemo(() => {
    if (allMedia.length === 0) {
      return [];
    }

    const selectedArray = currentArray?.name;
    return allMedia
      .filter((m) => m?.media?.length && m.media.length > 0)
      .map((m) => {
        return {
          name: m.name,
          select: () => {
            selectArray(m.name);
          },
          active: m.name === selectedArray,
        };
      });
  }, [allMedia, currentArray]);

  const swipeable = useSwipeable({
    onSwipedRight: decrement,
    onSwipedLeft: increment,
    ...config,
  });

  return {
    media,
    carousels,
    setAllMedia,
    selectArray,
    decrement,
    findMedia,
    increment,
    selectedMedia: (currentMedia && currentMedia[selectedMedia || 0]) || null,
    close,
    isOpen: modal.isOpen,
    swipeable,
  };
};

export default useMediaGallery;
